import { NgClass, NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    inject,
} from '@angular/core';

import { SixPackGroup } from '@cds/betting-offer/grouping/grid-view';
import { EventModel, EventOptionGroup, PeriodGameScore, ScoreboardType, SetGameScore } from '@frontend/sports/betting-offer/feature/model';
import { OfferGroupingService } from '@frontend/sports/betting-offer/feature/offer-grouping';
import { MarqueeConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { hasTarget } from '@frontend/sports/common/core/utils/dom';
import { Group } from '@frontend/sports/grid/core/feature/model';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { RemoveHrefDirective } from '@frontend/sports/navigation/core/feature/remove-href';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { ParticipantImageComponent } from '@frontend/sports/participant-image/feature';
import { TrackingSource } from '@frontend/sports/tracking/feature';
import { MarqueeVariantVersions } from '@frontend/sports/types/components/highlights-marquee';
import { DsTouchClickDirective } from '@frontend/ui/shared';
import { ContentReadyTriggerDirective, DeviceService, NativeAppService, PlainLinkComponent, WINDOW } from '@frontend/vanilla/core';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { keys } from 'lodash-es';

import { ScoreboardTimeComponent } from '../../card-scoreboards/scoreboard-time.component';
import { OptionGroupService } from '../../event-model/helpers/option-group.service';
import { LcpCandidateDirective } from '../../perf-cdk/lcp-candidate.directive';
import { Tile } from '../../shared-models/tile.model';
import { HighlightsMarqueePage } from '../highlights-marquee-page.enum';
import { HighlightsMarqueePageService } from '../highlights-marquee-page.service';
import { MarqueeHelperService } from '../marquee-helper.service';
import { FixtureTile } from '../marquee-tile.model';
import { BetNowButtonComponent } from '../option-group/bet-now-button.component';
import { SixpackOptionGroupComponent } from './option-group/six-pack-option-group.component';

@Component({
    selector: 'ms-six-pack-event',
    templateUrl: './six-pack-event.component.html',
    styleUrl: './six-pack-event.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    hostDirectives: [LcpCandidateDirective, ContentReadyTriggerDirective],
    imports: [
        NgOptimizedImage,
        NgClass,
        NgTemplateOutlet,
        IconCustomComponent,
        ScoreboardTimeComponent,
        PlainLinkComponent,
        DsTouchClickDirective,
        RemoveHrefDirective,
        ParticipantImageComponent,
        BetNowButtonComponent,
        SixpackOptionGroupComponent,
    ],
})
export class SixPackEventComponent implements OnChanges, OnInit {
    @Input() page?: HighlightsMarqueePage;
    @Input() event: EventModel;
    @Input() trackingPosition: number;
    @Input() backgroundImage?: string;
    @Input() textColor?: string;
    @Input() tile: FixtureTile;
    @Output() sixPackFallbacktoRegularTile = new EventEmitter<{ tile: Tile; marketType: string }>();
    @HostBinding('class') className = 'six-pack-event six-pack-event-group ms-active-highlight';

    eventUrl: string;
    groupsMap: { [key: string]: EventOptionGroup };
    group: Group;
    promotedFixtureIdsMap: { [fixtureId: number]: number } = {};
    empty: boolean;
    decorateAttribute: boolean;
    participant1Score = '0';
    participant2Score = '0';
    liveScores: PeriodGameScore | SetGameScore;
    isSixpackMarketMarquee = true;
    scoreboardTypes = ScoreboardType;
    marketType: string;
    shouldRemoveHref: boolean;
    trackingSource: string;
    tracking: TrackingSource;
    private readonly _window = inject(WINDOW);
    public sitecore = inject(Sitecore);
    private urlHelper = inject(UrlHelperService);
    private optionGroupService = inject(OptionGroupService);
    private deviceService = inject(DeviceService);
    private nativeAppService = inject(NativeAppService);
    private redirectHelper = inject(RedirectHelperService);
    private grouping = inject(OfferGroupingService);
    public marqueeConfig = inject(MarqueeConfig);
    private marqueeHelperService = inject(MarqueeHelperService);
    private changeDetector = inject(ChangeDetectorRef);
    protected highlightsMarqueePageService = inject(HighlightsMarqueePageService);

    ngOnInit(): void {
        this.shouldRemoveHref = this.urlHelper.shouldRemoveHref();
        this.trackingSource = this.page ? this.highlightsMarqueePageService.mapPageSource(this.page).toLowerCase() + '/' : '';
    }

    eventClick(event: Event): void {
        if (!this.deviceService.isTouch && !this.nativeAppService.isTerminal) {
            if (this.tile?.isFromPortal) {
                const eventUrl = this.urlHelper.getEventUrl(this.tile.event);
                this._window.parent.postMessage({ eventUrl }, '*');

                return;
            }
            if (hasTarget(event, HTMLAnchorElement)) {
                return;
            }
            this.redirectHelper.goToPage(this.eventUrl);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.event) {
            this.getGridGrouping();

            if (this.event.scoreboard.type === ScoreboardType.BaseballGame) {
                this.liveScores = this.event.scoreboard.getScore<SetGameScore>()!;
                if (this.liveScores) {
                    this.participant1Score = this.liveScores.participant1.points ?? '0';
                    this.participant2Score = this.liveScores.participant2.points ?? '0';
                }
            } else {
                this.liveScores = this.event.scoreboard.getScore<PeriodGameScore>()!;
                if (this.liveScores) {
                    this.participant1Score = this.liveScores.participant1.total?.toString() ?? '0';
                    this.participant2Score = this.liveScores.participant2.total?.toString() ?? '0';
                }
            }
            this.setLink();
            this.setTrackingDetails();
        }
    }

    getParticipantScore(participant: number): string | number {
        return participant === 0 ? this.participant1Score : this.participant2Score;
    }

    trackGroup(index: number, group: Group): string {
        return group.id;
    }

    emptySiblings(group: Group): boolean {
        const map = this.getEmptyMap(group);

        return map.length === 2 && Math.abs(map[0] - map[1]) === 1;
    }

    emptySixPack(group: Group): boolean {
        const map = this.getEmptyMap(group);

        return map.length === 3;
    }

    getParticipantName(participantIndx: number): string {
        return this.marqueeHelperService.getParticipantName(participantIndx, this.event);
    }

    get baseballScore(): SetGameScore | undefined {
        return this.event.scoreboard.type === ScoreboardType.BaseballGame ? this.event.scoreboard.getScore<SetGameScore>() : undefined;
    }

    private setTrackingDetails(): void {
        this.tracking = {
            source: this.trackingSource,
            position: this.trackingPosition,
            marqueeName: this.tile.trackingDetail?.marqueeName,
            sitecoreTemplateId: this.tile.trackingDetail?.sitecoreTemplateId,
            marqueeType: this.tile.source?.marqueeType,
            isAutomatedMarquee: this.tile.source?.isAutomatedMarquee,
        };
    }

    private getEmptyMap(group: Group): number[] {
        if (!group?.childGroups) {
            return [];
        }

        return group.childGroups
            .map((id, index) => (!this.groupsMap[id]?.online && (!this.isV2MarqueeVariant || !this.groupsMap[id]?.options?.length) ? index : -1))
            .filter((x) => x !== -1);
    }

    private getGridGrouping(): void {
        this.grouping.getGridGrouping(this.event.sport.id).subscribe((config) => {
            const { sixPackGroups } = config || {
                sixPackGroups: new Array<SixPackGroup>(),
            };
            const gridGroup = this.marqueeConfig.sixPackGridGroupMapping?.find((grouping) => grouping.sportId === this.event.sport.id);
            const gameLinesGroup = sixPackGroups.find((group) => group.id === gridGroup?.gridGroupId);
            if (
                this.marqueeConfig.isFallbackMarqueeEnabled &&
                !this.tile.source?.isAutomatedMarquee &&
                this.isSixpackToRegularTile(gameLinesGroup?.marketGroupIds)
            ) {
                this.sixPackFallbacktoRegularTile.emit({
                    tile: this.tile,
                    marketType: this.marketType,
                });

                return;
            }
            this.group = this.getGroup(gameLinesGroup) ?? this.group;
            this.setOptionGroup();
            this.setLink();

            this.changeDetector.markForCheck();
        });
    }

    private setLink(): void {
        this.eventUrl = !this.tile?.isFromPortal ? this.urlHelper.getEventUrl(this.event) : '';
    }

    private setOptionGroup(): void {
        this.groupsMap = {};
        if (!this.group) {
            this.empty = true;
        } else {
            this.group.childGroups?.forEach((current) => {
                const optionGroup = this.optionGroupService.get(this.event.optionGroups, this.event.sport.id, this.event.live, current);
                const orderedOptionGroup = this.optionGroupService.orderBasedOnParticipants(
                    optionGroup,
                    this.event.participants,
                    this.event.viewType,
                );

                if (orderedOptionGroup) {
                    this.groupsMap[current] = orderedOptionGroup;
                }
            });

            const optionGroups = keys(this.groupsMap);
            const optionGroupsFilter = (id: string) =>
                !this.groupsMap[id]?.online && (!this.isV2MarqueeVariant || !this.groupsMap[id]?.options?.length);

            this.empty = optionGroups?.length === 0 || optionGroups?.every(optionGroupsFilter);
            this.decorateAttribute = true;
        }
    }

    private getGroup(group: SixPackGroup | undefined): Group | undefined {
        if (!group) {
            return undefined;
        }

        return {
            id: group.id,
            name: group.name,
            options: this.getOverriddenOptionNames(group.marketGroupIds, group.optionNames),
            balancedMarket: group.balancedMarketsEnabled,
            marketAttribute: group.showAttribute,
            childGroups: 'marketGroupIds' in group ? group.marketGroupIds : [],
            active: true,
            extended: false,
            visible: true,
            optionsToShow: group.optionsToShow,
            isFallbackGroup: group.isFallbackGroup,
            fallbackGridGroupIds: group.fallbackGridGroupIds,
        };
    }

    private getOverriddenOptionNames(gridGroupIds: string[], optionNames: string[]): string[] {
        return gridGroupIds.map((gridGroupId, index) => {
            const optionName = this.marqueeConfig.marqueeMarketOverrides[gridGroupId];

            return optionName ?? optionNames[index];
        });
    }

    private isSixpackToRegularTile(optionGroups: string[] | undefined): boolean {
        const optionGroupCollection: EventOptionGroup[] = [];
        let moneyLineOption: boolean;
        let totalOption: boolean;
        let spreadOption: boolean;
        if (optionGroups) {
            optionGroups?.forEach((current) => {
                const optionGroup = this.optionGroupService.get(this.event.optionGroups, this.event.sport.id, this.event.live, current);

                if (optionGroup) {
                    optionGroupCollection.push(optionGroup);
                    this.marketType = optionGroup.name;
                }
            });
            if (optionGroupCollection.length === 3) {
                moneyLineOption = this.marqueeHelperService.checkOddsThreshold(optionGroupCollection[0]);
                totalOption = this.marqueeHelperService.checkOddsThreshold(optionGroupCollection[1]);
                spreadOption = this.marqueeHelperService.checkOddsThreshold(optionGroupCollection[2]);
                if ((moneyLineOption && totalOption) || (totalOption && spreadOption) || (moneyLineOption && spreadOption)) {
                    return true;
                }
            } else if (optionGroupCollection.length === 1) {
                return true;
            } else if (optionGroupCollection.length === 0 && this.tile.fallbackMarketType) {
                this.marketType = this.tile.fallbackMarketType;

                return true;
            }
        }

        return false;
    }

    get isV2MarqueeVariant() {
        return this.marqueeConfig.marqueeVariant === MarqueeVariantVersions.V2;
    }
}
